import React from "react"
import { Check } from "lucide-react"

interface StepperProps {
  currentStep: number
  steps: {
    label: string
    description?: string
  }[]
  /** Called when a navigable step is clicked. Omit to keep the stepper read-only. */
  onStepClick?: (step: number) => void
  /** Decides which steps are reachable. Only consulted when onStepClick is provided. */
  canNavigate?: (step: number) => boolean
}

export function Stepper({ currentStep, steps, onStepClick, canNavigate }: StepperProps) {
  return (
    <div className="w-full overflow-x-auto pb-2">
      <ol className="flex w-full min-w-max md:min-w-0 justify-between">
        {steps.map((step, index) => {
          const stepNumber = index + 1
          const isActive = currentStep === stepNumber
          const isCompleted = currentStep > stepNumber
          const isNavigable = Boolean(onStepClick)
            && !isActive
            && (canNavigate ? canNavigate(stepNumber) : true)

          const handleClick = () => {
            if (!isNavigable) return
            onStepClick?.(stepNumber)
          }

          return (
            <li key={step.label} className="relative flex flex-col items-center flex-1 min-w-[80px]">
              {/* Step circle */}
              <div
                role={isNavigable ? "button" : undefined}
                tabIndex={isNavigable ? 0 : undefined}
                aria-current={isActive ? "step" : undefined}
                aria-disabled={onStepClick && !isNavigable ? true : undefined}
                title={isNavigable ? `Go to ${step.label}` : undefined}
                onClick={handleClick}
                onKeyDown={(event) => {
                  if (!isNavigable) return
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    handleClick()
                  }
                }}
                className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 z-10 transition-all duration-200 ${isActive
                  ? "border-primary bg-primary text-primary-foreground shadow-md shadow-primary/20"
                  : isCompleted
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-muted-foreground/30 bg-white text-muted-foreground"
                  } ${isNavigable
                    ? "cursor-pointer hover:scale-105 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    : onStepClick && !isActive
                      ? "cursor-not-allowed opacity-60"
                      : ""
                  }`}
              >
                {isCompleted ? <Check className="h-5 w-5" /> : <span>{stepNumber}</span>}
              </div>

              {/* Line connecting steps */}
              {index < steps.length - 1 && (
                <div className="absolute top-5 left-1/2 w-full h-[2px] -z-0">
                  <div
                    className={`h-full ${isCompleted ? "bg-primary" : "bg-muted-foreground/30"
                      }`}
                  />
                </div>
              )}

              {/* Step label and description */}
              <div className="mt-3 text-center px-1">
                <p
                  className={`text-sm font-medium ${isActive ? "text-primary font-semibold" :
                    isCompleted ? "text-foreground" : "text-muted-foreground"
                    }`}
                >
                  {step.label}
                </p>
                {step.description && (
                  <p className={`text-xs mt-0.5 ${isActive ? "text-foreground" : "text-muted-foreground"
                    }`}>
                    {step.description}
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
